import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, LinearProgress, Breadcrumbs, 
    Link, Stack, FormHelperText, Grid, MenuItem, InputLabel, FormControl, Select,
    Chip
} from '@mui/material';
import { Home, InsertPhoto, CalendarToday } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ImageUploader from '../../common/ImageUploader';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import { getImageUrl } from '../../../../services/api';
import offerApi from '../../../../services/offerApi';

const OfferBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        discount: '',
        validFrom: null,
        validUntil: null,
        status: 'active',
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [errors, setErrors] = useState({ 
        title: '', 
        discount: '', 
        validFrom: '', 
        validUntil: '',
        image: '' 
    });
    const [touched, setTouched] = useState({ 
        title: false, 
        discount: false, 
        validFrom: false, 
        validUntil: false,
        image: false 
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNew) {
            const fetchOffer = async () => {
                try {
                    const offer = await offerApi.getById(id);
                    setFormData({
                        title: offer.title || '',
                        description: offer.description || '',
                        discount: offer.discount || '',
                        validFrom: offer.validFrom ? new Date(offer.validFrom) : null,
                        validUntil: offer.validUntil ? new Date(offer.validUntil) : null,
                        status: offer.status || 'active'
                    });
    
                    if (offer.imageUrl) {
                        const fullUrl = getImageUrl(offer.imageUrl);
                        setPreviewUrl(fullUrl);
                        setExistingImageUrl(fullUrl);
                    }
                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load offer', 'error');
                    navigate('/admin/offers');
                } finally {
                    setLoading(false);
                }
            };
            fetchOffer();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'title': 
                return value.trim() ? '' : 'Title is required';
            case 'discount': 
                return value.trim() ? '' : 'Discount is required';
            case 'validFrom': 
                return value ? '' : 'Start date is required';
            case 'validUntil': 
                if (!value) return 'End date is required';
                if (formData.validFrom && value < formData.validFrom) {
                    return 'End date must be after start date';
                }
                return '';
            case 'image': 
                return (isNew ? !previewUrl : (!previewUrl && !existingImageUrl)) 
                    ? 'Image is required' 
                    : '';
            default: 
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleDateChange = (name, date) => {
        setFormData(prev => ({ ...prev, [name]: date }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, date) }));
        }
        
        // Validate the other date field if both are set
        if (name === 'validFrom' && formData.validUntil) {
            setErrors(prev => ({ 
                ...prev, 
                validUntil: validateField('validUntil', formData.validUntil) 
            }));
        } else if (name === 'validUntil' && formData.validFrom) {
            setErrors(prev => ({ 
                ...prev, 
                validFrom: validateField('validFrom', formData.validFrom) 
            }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleImageSelect = (file) => {
        if (!file) return;
        
        if (previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, image: file }));
        setPreviewUrl(objectUrl);
        setErrors(prev => ({ ...prev, image: '' }));
    };

    const handleSubmit = async () => {
        // Mark all fields as touched
        setTouched({
            title: true,
            discount: true,
            validFrom: true,
            validUntil: true,
            image: true
        });
        
        // Validate all fields
        const newErrors = {
            title: validateField('title', formData.title),
            discount: validateField('discount', formData.discount),
            validFrom: validateField('validFrom', formData.validFrom),
            validUntil: validateField('validUntil', formData.validUntil),
            image: validateField('image', formData.image)
        };
        
        setErrors(newErrors);
        
        // Check if there are any errors
        if (Object.values(newErrors).some(error => error)) {
            return;
        }
      
        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this offer?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });
      
        if (!confirm.isConfirmed) return;
      
        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('discount', formData.discount);
            formDataToSend.append('validFrom', formData.validFrom.toISOString());
            formDataToSend.append('validUntil', formData.validUntil.toISOString());
            formDataToSend.append('status', formData.status);
            
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }
      
            if (isNew) {
                await offerApi.create(formDataToSend);
            } else {
                await offerApi.update(id, formDataToSend);
            }
      
            Swal.fire('Success', `Offer ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/offers');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/offers')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="#" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Master Management
                            </Link>
                            <Link href="/admin/offers" underline="hover" color="inherit">
                                Offers
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>
                
                <Divider />
                
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Left Column - Form Fields */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Offer Details
                            </Typography>
                            
                            <TextField
                                required
                                fullWidth
                                label="Offer Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <TextField
                                required
                                fullWidth
                                label="Discount"
                                name="discount"
                                value={formData.discount}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.discount && Boolean(errors.discount)}
                                helperText={touched.discount && errors.discount}
                                sx={{ mb: 3 }}
                                size="small"
                                placeholder="e.g. 20% OFF"
                            />
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label="Start Date *"
                                            value={formData.validFrom}
                                            onChange={(date) => handleDateChange('validFrom', date)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    error={touched.validFrom && Boolean(errors.validFrom)}
                                                    helperText={touched.validFrom && errors.validFrom}
                                                    onBlur={() => setTouched(prev => ({ ...prev, validFrom: true }))}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label="End Date *"
                                            value={formData.validUntil}
                                            onChange={(date) => handleDateChange('validUntil', date)}
                                            minDate={formData.validFrom || undefined}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    error={touched.validUntil && Boolean(errors.validUntil)}
                                                    helperText={touched.validUntil && errors.validUntil}
                                                    onBlur={() => setTouched(prev => ({ ...prev, validUntil: true }))}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                            
                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status}
                                    label="Status"
                                    onChange={handleInputChange}
                                    name="status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                    <MenuItem value="upcoming">Upcoming</MenuItem>
                                </Select>
                            </FormControl>
                            
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                                Upload Image
                            </Typography>
                            
                            <Box sx={{ 
                                p: 2,
                                border: '1px dashed',
                                borderColor: errors.image && touched.image ? 'error.main' : 'grey.300',
                                borderRadius: 1,
                                backgroundColor: 'grey.50',
                                textAlign: 'center'
                            }}>
                                <ImageUploader 
                                    onFileSelect={handleImageSelect}
                                    accept="image/*"
                                    maxSize={5 * 1024 * 1024}
                                />
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                    Supported formats: JPEG, PNG, GIF
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Maximum size: 5MB
                                </Typography>
                                {errors.image && touched.image && (
                                    <FormHelperText error sx={{ mt: 1 }}>{errors.image}</FormHelperText>
                                )}
                            </Box>
                        </Grid>

                        {/* Right Column - Image Preview */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Image Preview
                            </Typography>
                            
                            <Box sx={{ 
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                height: '100%',
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {previewUrl || existingImageUrl ? (
                                    <>
                                        <Box sx={{ 
                                            flexGrow: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative'
                                        }}>
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Offer Preview"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: 250,
                                                    objectFit: 'contain',
                                                    borderRadius: 4
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                                    opacity: 0,
                                                    transition: 'opacity 0.3s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 1 }
                                                }}
                                                onClick={() => window.open(previewUrl || existingImageUrl, '_blank')}
                                            >
                                                <Typography variant="caption" color="white">
                                                    Click to view full size
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Offer Details Preview</Typography>
                                            <Typography variant="h6" sx={{ mt: 1 }}>{formData.title || 'Offer Title'}</Typography>
                                            {formData.discount && (
                                                <Chip
                                                    label={formData.discount} 
                                                    color="primary" 
                                                    size="small"
                                                    sx={{ mt: 1, fontWeight: 600 }}
                                                />
                                            )}
                                            {formData.description && (
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {formData.description}
                                                </Typography>
                                            )}
                                            {(formData.validFrom || formData.validUntil) && (
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                                    <CalendarToday fontSize="small" color="action" />
                                                    <Typography variant="caption">
                                                        {formData.validFrom ? formData.validFrom.toLocaleDateString() : 'Start date'} - 
                                                        {formData.validUntil ? formData.validUntil.toLocaleDateString() : 'End date'}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ 
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        color: 'text.secondary'
                                    }}>
                                        <InsertPhoto sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography>No image selected</Typography>
                                        <Typography variant="caption" sx={{ mt: 1 }}>
                                            Upload an image to see preview
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <Divider />
                
                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/offers')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OfferBody;